/**
 * Host login/session plumbing — magic-link auth against the worker's
 * /api/auth/* endpoints (Supabase behind them, but the SPA only ever talks to
 * the worker). Guests never touch any of this: voter codes and host tokens
 * stay exactly as they were.
 *
 * Session lives in localStorage; `authFetch` attaches the bearer token and
 * transparently refreshes an expired access token once.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const STORAGE_KEY = 'bfn_session_v1';

// reconcile with shared/protocol.ts — auth session shape from POST /api/auth/verify
export interface Session {
  access_token: string;
  refresh_token: string;
  /** epoch seconds when the access token expires */
  expires_at: number;
  email: string;
}

type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();

function notify(s: Session | null) {
  for (const l of listeners) l(s);
}

/** Subscribe to session changes (header sign-in state). Returns unsubscribe. */
export function onSessionChange(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function normalizeExpiry(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    // tolerate ms-epoch or an expires_in style duration
    if (raw > 10_000_000_000) return Math.floor(raw / 1000);
    if (raw < 1_000_000_000) return Math.floor(Date.now() / 1000) + raw;
    return Math.floor(raw);
  }
  if (typeof raw === 'string') {
    const t = Date.parse(raw);
    if (!Number.isNaN(t)) return Math.floor(t / 1000);
  }
  // unknown — assume an hour, refresh will sort it out
  return Math.floor(Date.now() / 1000) + 3600;
}

function parseSession(raw: unknown, fallbackEmail?: string): Session | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const access = o.access_token;
  const refresh = o.refresh_token;
  if (typeof access !== 'string' || typeof refresh !== 'string') return null;
  const email = typeof o.email === 'string' ? o.email : (fallbackEmail ?? '');
  return {
    access_token: access,
    refresh_token: refresh,
    expires_at: normalizeExpiry(o.expires_at),
    email,
  };
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parseSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function setSession(s: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  notify(s);
}

export function signOut(): void {
  localStorage.removeItem(STORAGE_KEY);
  notify(null);
}

export function isSignedIn(): boolean {
  return getSession() !== null;
}

/** POST /api/auth/login — sends the magic-link email. */
export async function requestLoginLink(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const raw = (await res.json().catch(() => null)) as {
    message?: string;
    sent?: boolean;
    reason?: string;
  } | null;
  if (!res.ok) {
    throw new Error(raw?.message ?? `Could not send the sign-in link (${res.status}).`);
  }
  // the worker always answers ok:true (no user enumeration) but tells us when
  // the mail pipeline isn't wired yet — don't show a lying "sent" state
  if (raw && raw.sent === false) {
    throw new Error(
      raw.reason === 'mail-unconfigured'
        ? 'Email isn’t connected yet — add a Resend API key to the worker to turn this on.'
        : 'The sign-in email could not be sent. Try again in a minute.',
    );
  }
}

/** POST /api/auth/verify — exchanges the ?auth= token_hash from the magic link. */
export async function verifyLoginToken(tokenHash: string): Promise<Session> {
  const res = await fetch(`${API_BASE}/api/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token_hash: tokenHash }),
  });
  const raw: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = (raw as { message?: string } | null)?.message;
    // Falls back only when the worker sent no message at all; it still has to
    // name a next step, because this string reaches the same dead-end screen.
    throw new Error(msg ?? 'That sign-in link did not work — ask for a fresh one below.');
  }
  const s = parseSession(raw);
  if (!s) throw new Error("That sign-in didn't complete — ask for a fresh link below.");
  setSession(s);
  return s;
}

let refreshing: Promise<Session | null> | null = null;

/** POST /api/auth/refresh — swap the refresh token for a fresh session. */
async function refreshSession(): Promise<Session | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const cur = getSession();
    if (!cur) return null;
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: cur.refresh_token }),
      });
      if (!res.ok) {
        if (res.status === 400 || res.status === 401) signOut();
        return null;
      }
      const raw: unknown = await res.json().catch(() => null);
      const s = parseSession(raw, cur.email);
      if (!s) return null;
      setSession(s);
      return s;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** The current access token, refreshed when it is about to expire (the avatar maker calls the
 *  ThursdayNight project's PostgREST/Storage directly with it). Throws when signed out. */
export async function accessToken(): Promise<string> {
  let session = getSession();
  if (session && session.expires_at - Date.now() / 1000 < 60) session = (await refreshSession()) ?? null;
  if (!session) throw new Error('not signed in');
  return session.access_token;
}

/**
 * Fetch with the bearer token attached. Refreshes proactively when the token
 * is about to expire and retries exactly once on a 401.
 */
export async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  let session = getSession();
  if (session && session.expires_at - Date.now() / 1000 < 60) {
    session = (await refreshSession()) ?? getSession();
  }
  const doFetch = (token: string | undefined) =>
    fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init?.headers ?? {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  let res = await doFetch(session?.access_token);
  if (res.status === 401 && session) {
    const fresh = await refreshSession();
    if (fresh) res = await doFetch(fresh.access_token);
  }
  return res;
}
